import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Flex,
  Button,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { getAllUsers } from '../api/auth';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';
import AddUserModal from '../components/addUserModal';
import DeleteUserModal from '../components/deleteUserModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import GroupsTab from '../components/groupsTab';
import useCurrentUser from '../hooks/useCurrentUser';
import { getApiErrorMessage } from '../utils/apiError';

const UsersTab = () => {
  const {
    data: users,
    error,
    isLoading,
    refetch,
  } = useQuery('users', getAllUsers, {
    refetchOnWindowFocus: false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  useEffect(() => {}, [users]);

  const handleOpenModal = (userId = null) => {
    setSelectedUserId(userId);
    setIsModalOpen(true);
  };

  const handleDeleteModalOpen = userId => {
    setSelectedUserId(userId);
    setIsDeleteModalOpen(true);
  };

  const handleChangePasswordModalOpen = user => {
    setSelectedUser(user);
    setIsChangePasswordModalOpen(true);
  };

  if (isLoading) {
    return (
      <Box
        w="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={5}
      >
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>An error occurred: {getApiErrorMessage(error)}</Text>
      </Box>
    );
  }

  return (
    <Box flex="1" minHeight={0} display="flex" flexDirection="column" w="100%">
      <TableContainer flex="1" minHeight={0} overflowY="auto" w="100%">
        <Table variant="simple" colorScheme="gray" size="sm" w="100%">
          <Thead position="sticky" top={0} zIndex={1} bg="white">
            <Tr>
              <Th px={4} py={2} textAlign="center">
                Enable
              </Th>
              <Th px={4} py={2}>
                User Name
              </Th>
              <Th px={4} py={2}>
                Full Name
              </Th>
              <Th px={4} py={2}>
                User Group
              </Th>
              <Th px={4} py={2}>
                Job Title
              </Th>
              <Th px={4} py={2}>
                Phone Number
              </Th>
              <Th px={4} py={2}>
                Description
              </Th>
              <Th px={4} py={2}>
                Action
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {Array.isArray(users) && users.length > 0 ? (
              users.map(user => (
                <Tr key={user.id} height="40px">
                  <Td px={4} py={2} textAlign="center">
                    {user.enabled ? (
                      <CheckIcon color="green.500" />
                    ) : (
                      <CloseIcon color="red.500" />
                    )}
                  </Td>
                  <Td px={4} py={2}>
                    {user.userName || '-'}
                  </Td>
                  <Td px={4} py={2}>
                    {user.fullName || '-'}
                  </Td>
                  <Td px={4} py={2}>
                    {user.role || '-'}
                  </Td>
                  <Td px={4} py={2}>
                    {user.jobTitle || '-'}
                  </Td>
                  <Td px={4} py={2}>
                    {user.phoneNumber || '-'}
                  </Td>
                  <Td px={4} py={2}>
                    {user.description || '-'}
                  </Td>
                  <Td px={4} py={2}>
                    <Flex align="center">
                      <Text
                        as="span"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => handleOpenModal(user.id)}
                      >
                        Edit
                      </Text>
                      <Text as="span" mx={2}>
                        |
                      </Text>
                      <Text
                        as="span"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => handleDeleteModalOpen(user.id)}
                      >
                        Delete
                      </Text>
                      <Text as="span" mx={2}>
                        |
                      </Text>
                      <Text
                        as="span"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => handleChangePasswordModalOpen(user)}
                      >
                        Change Password
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={8}>
                  <Text textAlign="center">No users found.</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Box mt={3} mb={4}>
        <Flex justifyContent="flex-end">
          <Button
            size="sm"
            colorScheme="gray"
            onClick={() => handleOpenModal(null)}
          >
            Add User
          </Button>
        </Flex>
      </Box>
      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        refetch={refetch}
        userId={selectedUserId}
      />
      <DeleteUserModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        userId={selectedUserId}
        refetch={refetch}
      />
      <ChangePasswordModal
        isOpen={isChangePasswordModalOpen}
        onClose={() => setIsChangePasswordModalOpen(false)}
        userId={selectedUser?.id}
        userName={selectedUser?.userName}
      />
    </Box>
  );
};

const UserListPage = () => {
  // Menüde gizlemek yetmiyor; adres çubuğundan doğrudan gelinebiliyor.
  const { isLoading, canManageUsers } = useCurrentUser();

  if (isLoading) {
    return (
      <Box
        w="100%"
        h="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner />
      </Box>
    );
  }

  if (!canManageUsers) {
    return (
      <Box w="100%" display="flex" justifyContent="center" p={5}>
        <Text>You do not have permission to view this page.</Text>
      </Box>
    );
  }

  return (
    <Box
      w="100%"
      h="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      p={4}
    >
      <Tabs
        variant="line"
        colorScheme="blue"
        isLazy
        display="flex"
        flexDirection="column"
        flex="1"
        minHeight={0}
      >
        <TabList>
          <Tab>Users</Tab>
          <Tab>Groups</Tab>
        </TabList>
        <TabPanels flex="1" minHeight={0} display="flex" flexDirection="column">
          <TabPanel
            px={0}
            flex="1"
            minHeight={0}
            display="flex"
            flexDirection="column"
          >
            <UsersTab />
          </TabPanel>
          <TabPanel
            px={0}
            flex="1"
            minHeight={0}
            display="flex"
            flexDirection="column"
          >
            <GroupsTab />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default UserListPage;
